/**
 * Payment Status Constants
 * 
 * This enum defines the consistent payment status values used across the application
 * for all documents (invoices, offers, tickets, etc.)
 * 
 * Usage:
 * - Use these constants when sending payment status to the backend
 * - Use these constants for UI display and filtering
 * - Do not use inconsistent values like "unpaid", "partially paid", etc.
 */

export const PAYMENT_STATUS = {
  PAID: 'Paid',
  NOT_PAID: 'Not Paid',
  PARTIAL_PAID: 'Partial Paid',
};

/**
 * Get payment status based on paid amount and total
 * @param {number} paidAmount - Amount already paid
 * @param {number} totalAmount - Total amount due
 * @returns {string} Payment status from PAYMENT_STATUS enum
 */
export const getPaymentStatus = (paidAmount, totalAmount) => {
  // ✅ Round both values to 2 decimal places to avoid floating point precision issues
  const paid = Math.round((Number(paidAmount) || 0) * 100) / 100;
  const total = Math.round((Number(totalAmount) || 0) * 100) / 100;
  
  if (paid === 0) {
    return PAYMENT_STATUS.NOT_PAID;
  } else if (paid >= total) {
    return PAYMENT_STATUS.PAID;
  } else if (paid > 0 && paid < total) {
    // Partial payment: amount paid is greater than 0 but less than total
    return PAYMENT_STATUS.PARTIAL_PAID;
  } else {
    return PAYMENT_STATUS.NOT_PAID; // Default fallback
  }
};

/**
 * Get payment status color for UI
 * @param {string} status - Payment status from PAYMENT_STATUS enum
 * @returns {string} MUI chip color
 */
export const getPaymentStatusColor = (status) => {
  switch (status) {
    case PAYMENT_STATUS.PAID:
      return 'success';
    case PAYMENT_STATUS.PARTIAL_PAID:
      return 'warning';
    case PAYMENT_STATUS.NOT_PAID:
      return 'error';
    default:
      return 'default';
  }
};

/**
 * Convert API status value to display value
 * @param {string} apiStatus - Status from backend API (handles various formats)
 * @returns {string} Display status from PAYMENT_STATUS enum
 */
export const apiStatusToDisplay = (apiStatus) => {
  const normalized = (apiStatus || '').toLowerCase().replace(/\s+/g, '');
  
  switch (normalized) {
    case 'paid':
      return PAYMENT_STATUS.PAID;
    case 'notpaid':
    case 'unpaid':
      return PAYMENT_STATUS.NOT_PAID;
    case 'partialpaid':
    case 'partiallypaid':
    case 'partial':
      return PAYMENT_STATUS.PARTIAL_PAID;
    default:
      // If already in correct format, return as is
      return Object.values(PAYMENT_STATUS).includes(apiStatus) ? apiStatus : PAYMENT_STATUS.NOT_PAID;
  }
};

/**
 * Convert display status to API value (returns same value since backend expects PAYMENT_STATUS format)
 * @param {string} displayStatus - Display status from PAYMENT_STATUS enum
 * @returns {string} API status value (same as PAYMENT_STATUS)
 */
export const displayStatusToApi = (displayStatus) => {
  // Backend expects the same format as PAYMENT_STATUS
  return displayStatus;
};

export default PAYMENT_STATUS;
